import { z } from 'zod';

// ---------- UK Tax Settings ----------
export const ukTaxSettingsSchema = z.object({
  utr: z
    .string()
    .regex(/^\d{10}$/, 'UTR must be 10 digits')
    .nullish(),
  niNumber: z
    .string()
    .regex(
      /^[A-Z]{2}\d{6}[A-D]$/,
      'NI number format: 2 letters + 6 digits + 1 letter (A-D)',
    )
    .nullish(),
  vatRegistered: z.boolean().optional(),
  vatNumber: z
    .string()
    .regex(/^GB\d{9}$|^\d{9}$/, 'VAT number must be 9 digits (optionally prefixed GB)')
    .nullish(),
  vatScheme: z.enum(['standard', 'flat_rate', 'cash']).nullish(),
  vatStaggerGroup: z.enum(['group_1', 'group_2', 'group_3']).nullish(),
  taxYearStart: z.string().nullish(),
  businessLegalStructure: z.enum(['sole_trader', 'ltd', 'partnership', 'llp']).nullish(),
  privateUseVehiclePercent: z.number().int().min(0).max(100).nullish(),
  privateUseHomeOfficePercent: z.number().int().min(0).max(100).nullish(),
  isCisRegistered: z.boolean().optional(),
  cisUtrSuffix: z
    .string()
    .regex(/^[A-Z]$/, 'CIS UTR suffix must be a single uppercase letter')
    .nullish(),
});

export type UkTaxSettingsInput = z.infer<typeof ukTaxSettingsSchema>;

// ---------- UK Tax Year Query ----------
export const ukTaxYearQuerySchema = z.object({
  taxYear: z
    .string()
    .regex(/^\d{4}\/\d{2}$/, 'UK tax year format: YYYY/YY (e.g. 2025/26)')
    .optional(),
});

export type UkTaxYearQuery = z.infer<typeof ukTaxYearQuerySchema>;

// ---------- VAT Period Query ----------
export const vatPeriodQuerySchema = z.object({
  periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
  periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
});

export type VatPeriodQuery = z.infer<typeof vatPeriodQuerySchema>;

// ---------- CIS Subcontractor Schemas ----------
export const cisSubcontractorCreateSchema = z.object({
  legalName: z.string().min(1, 'Legal name is required').max(255),
  tradingName: z.string().max(255).optional(),
  utr: z
    .string()
    .regex(/^\d{10}$/, 'UTR must be 10 digits')
    .optional(),
});

export const cisSubcontractorUpdateSchema = z.object({
  legalName: z.string().min(1).max(255).optional(),
  tradingName: z.string().max(255).nullish(),
  utr: z
    .string()
    .regex(/^\d{10}$/, 'UTR must be 10 digits')
    .optional(),
});

export const cisVerificationSchema = z.object({
  cisVerificationNumber: z.string().min(1, 'Verification number is required'),
  cisDeductionRate: z
    .number()
    .int()
    .refine((v) => v === 0 || v === 2000 || v === 3000, {
      message: 'Deduction rate must be 0 (gross), 2000 (20%), or 3000 (30%)',
    }),
});

export type CisSubcontractorCreateInput = z.infer<typeof cisSubcontractorCreateSchema>;
export type CisSubcontractorUpdateInput = z.infer<typeof cisSubcontractorUpdateSchema>;
export type CisVerificationInput = z.infer<typeof cisVerificationSchema>;

// ---------- CIS300 Query ----------
export const cis300QuerySchema = z.object({
  periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
  periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
});

export type Cis300Query = z.infer<typeof cis300QuerySchema>;

// ---------- UK Filing Draft ----------
export const ukFilingDraftQuerySchema = z.object({
  filingType: z
    .enum(['VAT_RETURN', 'SA103', 'CT600_SUMMARY', 'CIS300'])
    .optional(),
  status: z
    .enum(['DRAFT', 'READY', 'FILED_VIA_INTEGRATION', 'FILED_EXTERNALLY', 'ARCHIVED'])
    .optional(),
});

export type UkFilingDraftQuery = z.infer<typeof ukFilingDraftQuerySchema>;

export const ukMarkFiledSchema = z.object({
  filedVia: z.enum(['qbo', 'xero', 'manual']),
  filedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
  evidence: z.string().max(1000).optional(),
});

export type UkMarkFiledInput = z.infer<typeof ukMarkFiledSchema>;
